import { useEffect, useRef, useState } from 'react';
import styles from './CurvedTextWithStars.module.scss';

interface TextLine {
  text: string;
  fontSize: number;
}

interface CurvedTextWithStarsProps {
  textLines: TextLine[];
  textColor?: string;
  strokeColor?: string;
  outlineColor?: string;
  shadowColor?: string;
  starsColor?: string;
  disableStars?: boolean;
  className?: string;
  starsPadding?: number;
  verticalSpacing?: number;
  curveIntensity?: number;
  viewBox?: string;
}

export const CurvedTextWithStars = ({
  textLines,
  textColor = '#047881',
  strokeColor = '#93e080',
  outlineColor = '#000000',
  shadowColor = '#00000080',
  starsColor = '#93e080',
  disableStars = false,
  className = '',
  starsPadding = 40,
  verticalSpacing = 110,
  curveIntensity = 20,
  viewBox = '0 0 556.52 290.78',
}: CurvedTextWithStarsProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [starsOffset, setStarsOffset] = useState({ left: 0, right: 0 });

  // Calculate stars position based on text width
  useEffect(() => {
    if (!svgRef.current || disableStars) return;

    const textElement = svgRef.current.querySelector('#measureText') as SVGTextElement;
    if (!textElement) return;

    try {
      const bbox = textElement.getBBox();
      const textWidth = bbox.width;
      const viewBoxWidth = 556.52;
      const centerX = viewBoxWidth / 2;

      // Calculate stars position: viewBox center +/- half text width + padding
      const leftStarsX = centerX - textWidth / 2 - starsPadding;
      const rightStarsX = centerX + textWidth / 2 + starsPadding;

      // Original positions: "left group" has x=528 (on the right!), "right group" has x=67 (on the left!)
      setStarsOffset({
        right: rightStarsX - 528.16,
        left: leftStarsX - 67.49,
      });
    } catch (e) {
      console.warn('Could not calculate text bounds', e);
    }
  }, [disableStars, starsPadding]);

  const generateCurvePath = (lineIndex: number) => {
    const baseY = 130 + lineIndex * verticalSpacing;
    return `M100,${baseY}c4-3,65.5-${curveIntensity},178.6-${curveIntensity}c111.3,0.6,170.8,${curveIntensity - 3},175.1,${curveIntensity}`;
  };

  const calculateStrokeWidth = (fontSize: number, layer: 'black' | 'green') => {
    const baseStrokeRatio = layer === 'black' ? 0.5 : 0.25; // 50% or 25% of fontSize
    return fontSize * baseStrokeRatio;
  };

  return (
    <svg
      ref={svgRef}
      className={`${styles.curvedTextWithStars} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
    >
      {/* Curve path definitions */}
      {textLines.map((_, index) => (
        <path
          key={`curve-${index}`}
          id={`textCurve${index + 1}`}
          d={generateCurvePath(index)}
          fill="transparent"
        />
      ))}

      {/* LAYER 1: Shadow */}
      {textLines.map((line, index) => (
        <text
          key={`shadow-${index}`}
          style={{
            fontSize: `${line.fontSize}px`,
            fill: 'none',
            stroke: shadowColor,
            strokeWidth: `${calculateStrokeWidth(line.fontSize, 'black')}px`,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
            transform: 'translateY(11px)',
            fontFamily: 'Mikado',
            fontWeight: 700,
          }}
        >
          <textPath href={`#textCurve${index + 1}`} startOffset="50%" textAnchor="middle">
            {line.text}
          </textPath>
        </text>
      ))}

      {/* LAYER 2: Outline */}
      {textLines.map((line, index) => (
        <text
          key={`black-${index}`}
          style={{
            fontSize: `${line.fontSize}px`,
            fill: 'none',
            stroke: outlineColor,
            strokeWidth: `${calculateStrokeWidth(line.fontSize, 'black')}px`,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
            transform: 'translateY(6px)',
            fontFamily: 'Mikado',
            fontWeight: 700,
          }}
        >
          <textPath href={`#textCurve${index + 1}`} startOffset="50%" textAnchor="middle">
            {line.text}
          </textPath>
        </text>
      ))}

      {/* LAYER 3: Stroke with knockout */}
      {textLines.map((line, index) => (
        <text
          key={`stroke-${index}`}
          style={{
            fontSize: `${line.fontSize}px`,
            fill: 'none',
            stroke: strokeColor,
            strokeWidth: `${calculateStrokeWidth(line.fontSize, 'green')}px`,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
            fontFamily: 'Mikado',
            fontWeight: 700,
            paintOrder: 'stroke fill',
          }}
        >
          <textPath href={`#textCurve${index + 1}`} startOffset="50%" textAnchor="middle">
            {line.text}
          </textPath>
        </text>
      ))}

      {/* LAYER 4: Fill - covers holes in letters */}
      {textLines.map((line, index) => (
        <text
          key={`fill-${index}`}
          id={index === textLines.length - 1 ? 'measureText' : undefined}
          style={{
            fontSize: `${line.fontSize}px`,
            fill: textColor,
            stroke: textColor,
            strokeWidth: '1px',
            fontFamily: 'Mikado',
            fontWeight: 700,
          }}
        >
          <textPath href={`#textCurve${index + 1}`} startOffset="50%" textAnchor="middle">
            {line.text}
          </textPath>
        </text>
      ))}

      {/* Stars with dynamic positioning */}
      {!disableStars && (
        <>
          {/* Right stars group */}
          <g transform={`translate(${starsOffset.right}, 0)`}>
            <path
              style={{ fill: starsColor }}
              d="M528.16,111.06c-14.03,5.1-17.29,9.65-19.3,29.88-.11,1.12-1.68,1.39-1.77.3-1.75-19.59-4.95-23.04-18.95-23.4-.97-.02-.96-1.45.01-1.8,14.03-5.1,17.29-9.65,19.3-29.88.11-1.12,1.68-1.39,1.77-.3,1.75,19.59,4.95,23.04,18.95,23.4.97.02.96,1.45-.01,1.8Z"
            />
            <path
              style={{ fill: starsColor }}
              d="M535.94,170.83c-6.34,2.31-7.81,4.36-8.72,13.5-.05.51-.76.63-.8.14-.79-8.85-2.24-10.41-8.56-10.57-.44-.01-.43-.66,0-.82,6.34-2.31,7.81-4.36,8.72-13.5.05-.51.76-.63.8-.14.79,8.85,2.24,10.41,8.56,10.57.44.01.43.66,0,.82Z"
            />
          </g>

          {/* Left stars group */}
          <g transform={`translate(${starsOffset.left}, 0)`}>
            <path
              style={{ fill: starsColor }}
              d="M67.49,64.88c-14.03,5.1-17.29,9.65-19.3,29.88-.11,1.12-1.68,1.39-1.77.3-1.75-19.59-4.95-23.04-18.95-23.4-.97-.02-.96-1.45.01-1.8,14.03-5.1,17.29-9.65,19.3-29.88.11-1.12,1.68-1.39,1.77-.3,1.75,19.59,4.95,23.04,18.95,23.4.97.02.96,1.45-.01,1.8Z"
            />
            <path
              style={{ fill: starsColor }}
              d="M74.13,119.08c-6.34,2.31-7.81,4.36-8.72,13.5-.05.51-.76.63-.8.14-.79-8.85-2.24-10.41-8.56-10.57-.44-.01-.43-.66,0-.82,6.34-2.31,7.81-4.36,8.72-13.5.05-.51.76-.63.8-.14.79,8.85,2.24,10.41,8.56,10.57.44.01.43.66,0,.82Z"
            />
          </g>
        </>
      )}
    </svg>
  );
};
